import type { FormPreviewSnapshot } from '@/contracts/form-preview'
import {
  type FormQuestionGroupInstanceContract,
  type QuestionGroupMutationState,
  type QuestionGroupTemplateContract,
  type QuestionOutlineItem,
  getQuestionGroupMutationState,
} from '@/contracts/question-groups'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import { normalizeFormPreviewSnapshot } from '@/lib/form-preview/normalize-preview-snapshot'
import { mockWorkspaceId } from '@/lib/mock-data'
import type { Form, LogicJump, Question } from '@/types/form'
import {
  createQuestionOutline,
  loadFixture,
  normalizeFormGroupInstances,
  savePatch,
} from './persistent-store'

export type CopyQuestionToGroupResult =
  | {
      ok: true
      question: Question
      group: FormQuestionGroupInstanceContract
      sourceMeta: ReturnType<typeof createSyntheticMeta>
    }
  | {
      ok: false
      code: 'group_not_found' | 'question_not_found' | 'synced_group_readonly'
      message: string
      sourceMeta: ReturnType<typeof createSyntheticMeta>
    }

function cloneQuestionForForm(
  question: Question,
  formId: string,
  groupInstanceId: string,
  orderIndex: number,
) {
  const nextQuestionId = crypto.randomUUID()
  return {
    ...structuredClone(question),
    id: nextQuestionId,
    formId,
    groupId: groupInstanceId,
    orderIndex,
    choices: question.choices.map((choice) => ({
      ...choice,
      id: crypto.randomUUID(),
      questionId: nextQuestionId,
    })),
  }
}

function cloneLogicJumpsForForm(logicJumps: LogicJump[], questionIdMap: Map<string, string>) {
  return logicJumps.map((jump, order) => ({
    ...structuredClone(jump),
    id: crypto.randomUUID(),
    questionId: questionIdMap.get(jump.questionId) ?? jump.questionId,
    targetQuestionId:
      jump.targetQuestionId === null
        ? null
        : (questionIdMap.get(jump.targetQuestionId) ?? jump.targetQuestionId),
    targetGroupId: jump.targetGroupId ?? null,
    displayTargets: jump.displayTargets?.map((target) => {
      if (target.type === 'group') return target
      const questionId = questionIdMap.get(target.questionId) ?? target.questionId
      if (target.type === 'group_question') {
        return {
          ...target,
          id: `group_question:${target.groupId}:${questionId}`,
          questionId,
        }
      }
      return {
        ...target,
        id: `question:${questionId}`,
        questionId,
      }
    }),
    conditions: jump.conditions.map((condition) => ({
      ...condition,
      field: questionIdMap.get(condition.field) ?? condition.field,
    })),
    order,
  }))
}

function getFormDetailOrEmpty(formId: string) {
  const state = loadFixture()
  return (
    state.formDetails[formId] ?? {
      questions: [],
      logicJumps: [],
      hiddenFields: {},
      groups: [],
      outline: [],
    }
  )
}

function saveQuestionGroupState(params: {
  formId: string
  questions: Question[]
  logicJumps: LogicJump[]
  groups: FormQuestionGroupInstanceContract[]
  outline?: QuestionOutlineItem[]
}) {
  const outline = params.outline ?? createQuestionOutline(params.questions, params.groups)
  const detail = getFormDetailOrEmpty(params.formId)

  return savePatch((current) => ({
    formDetails: {
      ...current.formDetails,
      [params.formId]: {
        ...detail,
        questions: params.questions,
        logicJumps: params.logicJumps,
        groups: params.groups,
        outline,
      },
    },
    questionGroupInstances: {
      ...current.questionGroupInstances,
      [params.formId]: params.groups,
    },
    questionOutlineOrder: {
      ...current.questionOutlineOrder,
      [params.formId]: outline,
    },
  }))
}

function updateTemplateUsage(templateId: string, delta = 1) {
  savePatch((current) => ({
    questionGroupTemplates: current.questionGroupTemplates.map((template) =>
      template.id === templateId
        ? {
            ...template,
            usageCount: Math.max(0, template.usageCount + delta),
            updatedAt: new Date().toISOString(),
          }
        : template,
    ),
  }))
}

export function listMockQuestionGroupTemplates(): QuestionGroupTemplateContract[] {
  return [...loadFixture().questionGroupTemplates].sort((left, right) =>
    left.title.localeCompare(right.title, 'zh-Hant'),
  )
}

export function getMockQuestionGroupTemplate(groupId: string) {
  return loadFixture().questionGroupTemplates.find((template) => template.id === groupId) ?? null
}

function createQuestionGroupPreviewForm(template: QuestionGroupTemplateContract): Form {
  return {
    id: template.id,
    workspaceId: template.workspaceId,
    title: template.title,
    description: template.description ?? '題組模板預覽',
    status: 'draft',
    themeConfig: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
      answerColor: '#475569',
      buttonColor: '#6366F1',
      buttonTextColor: '#FFFFFF',
      accentColor: '#6366F1',
      radius: 8,
      maxWidth: 720,
      fontFamily: 'Geist, Inter, sans-serif',
      progressStyle: 'thin',
    },
    settings: {
      showProgress: true,
      layoutWidth: 720,
    },
    welcomeScreen: {
      title: template.title,
      description: template.description ?? '題組模板開始頁。',
    },
    thankYouScreen: {
      title: '題組預覽完成',
      description: '此頁只用於題組流程檢視，不會建立問卷回應。',
      animationStyle: 'minimal',
    },
    createdBy: 'mock-question-group-template',
    updatedAt: template.updatedAt,
  }
}

export function createQuestionGroupPreviewSnapshot(
  template: QuestionGroupTemplateContract,
  options: { includeWelcomeCompletion?: boolean } = {},
): FormPreviewSnapshot {
  const questions = template.questions.map((question, orderIndex) => ({
    ...question,
    formId: template.id,
    groupId: template.id,
    orderIndex,
    choices: question.choices.map((choice, choiceIndex) => ({
      ...choice,
      questionId: question.id,
      orderIndex: choiceIndex,
    })),
  }))
  const group = {
    id: template.id,
    formId: template.id,
    sourceGroupId: template.id,
    mode: 'synced' as const,
    title: template.title,
    description: template.description,
    orderIndex: 0,
    collapsed: false,
    questionIds: questions.map((question) => question.id),
    questions,
    logicJumps: template.logicJumps,
    lockedReason: '這是同步題組模板預覽；題目變更會影響之後引用此題組的問卷。',
    sourceMeta: template.sourceMeta,
  }

  return normalizeFormPreviewSnapshot({
    id: `${template.id}:question-group-template:${options.includeWelcomeCompletion ? 'flow' : 'group'}`,
    source: 'question_group_template',
    mode: 'full_flow',
    form: createQuestionGroupPreviewForm(template),
    questions,
    groups: [group],
    outline: [{ kind: 'group', groupInstanceId: group.id }],
    logicJumps: template.logicJumps,
    hiddenFields: {},
    sourceMeta: template.sourceMeta,
    includeWelcomeCompletion: options.includeWelcomeCompletion ?? false,
  })
}

export function getMockQuestionGroupPreviewSnapshot(
  groupId: string,
  options: { includeWelcomeCompletion?: boolean } = {},
) {
  const template = getMockQuestionGroupTemplate(groupId)
  return template ? createQuestionGroupPreviewSnapshot(template, options) : null
}

export function createMockQuestionGroupTemplate(input: {
  title: string
  description?: string
  category?: string
  tags?: string[]
}) {
  const template: QuestionGroupTemplateContract = {
    id: `qgt-${crypto.randomUUID()}`,
    workspaceId: mockWorkspaceId,
    title: input.title,
    description: input.description,
    category: input.category,
    tags: input.tags ?? [],
    questionIds: [],
    questions: [],
    logicJumps: [],
    usageCount: 0,
    updatedAt: new Date().toISOString(),
    sourceMeta: createSyntheticMeta('2026-05-21'),
  }

  savePatch((current) => ({
    questionGroupTemplates: [template, ...current.questionGroupTemplates],
  }))

  return template
}

export function updateMockQuestionGroupTemplate(template: QuestionGroupTemplateContract) {
  const normalizedTemplate: QuestionGroupTemplateContract = {
    ...template,
    questionIds: template.questions.map((question) => question.id),
    questions: template.questions.map((question, orderIndex) => ({
      ...question,
      formId: template.id,
      orderIndex,
    })),
    logicJumps: template.logicJumps.map((jump, order) => ({ ...jump, order })),
    updatedAt: new Date().toISOString(),
    sourceMeta: createSyntheticMeta('2026-05-21'),
  }

  savePatch((current) => ({
    questionGroupTemplates: current.questionGroupTemplates.map((item) =>
      item.id === normalizedTemplate.id ? normalizedTemplate : item,
    ),
  }))

  return normalizedTemplate
}

export function listMockFormQuestionGroupInstances(formId: string) {
  const state = loadFixture()
  return state.questionGroupInstances[formId] ?? normalizeFormGroupInstances(formId, [])
}

export function getMockQuestionOutline(formId: string) {
  const state = loadFixture()
  const detail = state.formDetails[formId]
  return (
    state.questionOutlineOrder[formId] ??
    (detail ? createQuestionOutline(detail.questions, detail.groups) : [])
  )
}

export function addSyncedGroupToForm(formId: string, groupId: string) {
  const template = getMockQuestionGroupTemplate(groupId)
  if (!template) return null

  const detail = getFormDetailOrEmpty(formId)
  const groups = normalizeFormGroupInstances(formId, detail.groups)
  const instance: FormQuestionGroupInstanceContract = {
    id: `qgi-${crypto.randomUUID()}`,
    formId,
    sourceGroupId: template.id,
    mode: 'synced',
    title: template.title,
    description: template.description,
    orderIndex: groups.length,
    collapsed: false,
    questionIds: template.questionIds,
    questions: template.questions,
    logicJumps: template.logicJumps,
    lockedReason: '同步題組需回到題組管理編輯，問卷內只可排序與設定跳轉。',
    sourceMeta: createSyntheticMeta('2026-05-21'),
  }
  const nextGroups = [...groups, instance]
  const outline = [
    ...getMockQuestionOutline(formId),
    { kind: 'group' as const, groupInstanceId: instance.id },
  ]

  saveQuestionGroupState({
    formId,
    questions: detail.questions,
    logicJumps: detail.logicJumps,
    groups: nextGroups,
    outline,
  })
  updateTemplateUsage(template.id)

  return instance
}

export function copyGroupToForm(formId: string, groupId: string) {
  const template = getMockQuestionGroupTemplate(groupId)
  if (!template) return null

  const detail = getFormDetailOrEmpty(formId)
  const groups = normalizeFormGroupInstances(formId, detail.groups)
  const instanceId = `qgi-${crypto.randomUUID()}`
  const startOrderIndex =
    detail.questions.reduce((max, question) => Math.max(max, question.orderIndex), -1) + 1
  const questionIdMap = new Map<string, string>()
  const clonedQuestions = template.questions.map((question, index) => {
    const cloned = cloneQuestionForForm(question, formId, instanceId, startOrderIndex + index)
    questionIdMap.set(question.id, cloned.id)
    return cloned
  })
  const clonedLogicJumps = cloneLogicJumpsForForm(template.logicJumps, questionIdMap)
  const instance: FormQuestionGroupInstanceContract = {
    id: instanceId,
    formId,
    sourceGroupId: template.id,
    mode: 'copy',
    title: `${template.title}（複本）`,
    description: template.description,
    orderIndex: groups.length,
    collapsed: false,
    questionIds: clonedQuestions.map((question) => question.id),
    questions: clonedQuestions,
    logicJumps: clonedLogicJumps,
    sourceMeta: createSyntheticMeta('2026-05-21'),
  }
  const nextGroups = [...groups, instance]
  const outline = [
    ...getMockQuestionOutline(formId),
    { kind: 'group' as const, groupInstanceId: instance.id },
  ]

  saveQuestionGroupState({
    formId,
    questions: [...detail.questions, ...clonedQuestions],
    logicJumps: [...detail.logicJumps, ...clonedLogicJumps],
    groups: nextGroups,
    outline,
  })
  updateTemplateUsage(template.id)

  return instance
}

export function getFormQuestionGroupMutationState(
  formId: string,
  groupInstanceId: string,
): QuestionGroupMutationState | null {
  const instance = listMockFormQuestionGroupInstances(formId).find(
    (group) => group.id === groupInstanceId,
  )
  return instance ? getQuestionGroupMutationState(instance) : null
}

export function copyQuestionToGroup(
  formId: string,
  questionId: string,
  groupInstanceId: string,
): CopyQuestionToGroupResult {
  const detail = getFormDetailOrEmpty(formId)
  const groups = normalizeFormGroupInstances(formId, detail.groups)
  const group = groups.find((item) => item.id === groupInstanceId)
  const sourceMeta = createSyntheticMeta('2026-05-21')

  if (!group) {
    return {
      ok: false,
      code: 'group_not_found',
      message: '找不到指定題組。',
      sourceMeta,
    }
  }

  const mutationState = getQuestionGroupMutationState(group)
  if (!mutationState.allowed) {
    return {
      ok: false,
      code: mutationState.code,
      message: mutationState.message,
      sourceMeta,
    }
  }

  const sourceQuestion = detail.questions.find((question) => question.id === questionId)
  if (!sourceQuestion) {
    return {
      ok: false,
      code: 'question_not_found',
      message: '找不到要複製的問題。',
      sourceMeta,
    }
  }

  const nextOrderIndex =
    detail.questions.reduce((max, question) => Math.max(max, question.orderIndex), -1) + 1
  const copiedQuestion = {
    ...cloneQuestionForForm(sourceQuestion, formId, group.id, nextOrderIndex),
    title: `${sourceQuestion.title}（題組複本）`,
  }
  const nextGroups = groups.map((item) =>
    item.id === group.id
      ? {
          ...item,
          questionIds: [...item.questionIds, copiedQuestion.id],
          questions: [...(item.questions ?? []), copiedQuestion],
        }
      : item,
  )
  const updatedGroup = nextGroups.find((item) => item.id === group.id) ?? group

  saveQuestionGroupState({
    formId,
    questions: [...detail.questions, copiedQuestion],
    logicJumps: detail.logicJumps,
    groups: nextGroups,
  })

  return {
    ok: true,
    question: copiedQuestion,
    group: updatedGroup,
    sourceMeta,
  }
}
